<template>
  <div>
    <div v-if="isPrescreenLanding">
      <router-view />
    </div>
    <div
      v-else
      id="wrapper"
      class="wrapper-app"
    >
      <!--    <environment-banner />-->
      <HeadTitle
        :title="`Personal Loan Application - ${settings.company_long_name}`"
      />

      <Header />

      <main>
        <prescreen-banner
          v-if="prospect.id"
          :prospect="prospect"
        />

        <Banner
          v-if="showBanner"
          :is-returning-customer="status.isReturningCustomer"
        />

        <section
          id="main"
          class="wrapper"
        >
          <div
            :class="innerStepClass"
            class="inner"
          >
            <div class="content">
              <!-- Processing -->
              <Processing v-if="status.processing" />

              <!-- Loading -->
              <Loading v-else-if="status.loading" />

              <!-- Forms -->
              <router-view v-else />
              <!-- ./Forms -->
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";

import Header from "@app/tbo_bank/components/Header.vue";
import Footer from "@app/tbo_bank/components/Footer.vue";
import Banner from "@app/tbo_bank/components/Banner.vue";
import FormGroup from "@app/tbo_bank/components/FormGroup.vue";
import PrescreenBanner from "@app/tbo_bank/components/PrescreenBanner.vue";

import { debounce } from "lodash";
import "@/assets/css/spacing.css";
import "@app/tbo_bank/assets/css/main.css";
import fullStoryMixin from "@app/utils/fullStoryMixin";
import Processing from "./components/Processing.vue";
import Loading from "./components/Loading.vue";

Vue.component("FormGroup", FormGroup);

export default {
  components: {
    Header,
    Footer,
    Banner,
    Processing,
    Loading,
    PrescreenBanner,
  },

  mixins: [fullStoryMixin],

  data() {
    return {
      customer_guid: null,
    };
  },

  computed: {
    ...mapState("underwriting", ["loan", "prospect", "status"]),
    ...mapState("settings", ["settings"]),
    isPrescreenLanding() {
      return this.$route.name === "prescreen";
    },
    innerStepClass() {
      return `inner-${this.$route.name}`;
    },
    showBanner() {
      if (this.$route.name === "error" || this.$route.name === "declined") {
        return false;
      }

      return (!this.status.loading && !this.status.error && !this.status.declined && !this.status.processing);
    },
    neededDefaultValues() {
      if (this.isReturningCustomer) {
        return this.settings?.default_values_returning_customer || false;
      }

      return this.settings?.default_values_normal_customer || false;
    }
  },

  watch: {
    loan: {
      deep: true,
      handler: debounce(function(value) {
        this.updateWalkupData(value);
      }, 1500),
    },

    $route: {
      deep: true,
      handler(value) {
        this.updateWalkupData(value);
      },
    },

    "status.declined": {
      deep: true,
      handler(value) {
        if (value) {
          this.$router.push("/declined");
        }
      }
    },

    "status.error": {
      deep: true,
      handler(value) {
        if (value) {
          this.$router.push("/error");
        }
      }
    }
  },

  async beforeMount() {
    await this.initDefaultParams();
    await this.getShortFormLead();
    await this.handleCustomer();
  },

  async mounted() {
    await this.$nextTick();

    setTimeout(async () => {
      const isReady = await this.initializeFullStory();

      if (isReady) {
        const email = this.loan?.email || null;

        const vars = email
          ? {
            email,
            userType: "returning",
            step: "app-init"
          }
          : {
            userType: "new",
            step: "app-init"
          };

        await this.setFullStoryVars(vars);
      }
    }, 1000);
  },

  methods: {
    ...mapActions("underwriting", ["setProspect", "updateLoan"]),
    ...mapActions("settings", ["setApiKey", "setCampaignId"]),

    initDefaultParams() {
      if (this.neededDefaultValues) {
        this.updateLoan({
          ismilitary: "false",
          paytype: "D",
          empphone: "(111)222-3333"
        });
      }
    },

    getCurrentStep(routeName) {
      const routeSplit = routeName.split("-");

      return routeSplit[1] || 0;
    },

    handleCustomer() {
      let walkupGuid = JSON.parse(localStorage.getItem("walkup-tracking"));
      walkupGuid = walkupGuid?.walkup_guid || null;

      if (this.prospect?.id) {
        this.getTrackByProspect(walkupGuid);
      } else if (!walkupGuid) {
        this.saveFirstTrack();
      } else {
        this.getTrackByGuid(walkupGuid);
      }
    },

    async updateWalkupData() {
      const walkupTrkg = JSON.parse(localStorage.getItem("walkup-tracking"));

      const payload = {
        prospectId: this.prospect?.id || null,
        walkupGuid: walkupTrkg?.walkup_guid || null,
        loanAppData: this.loan,
        currentStep: this.getCurrentStep(this.$route.name),
        isWalkupCompleted: 0,
        ...this.getUtmParams()
      };

      const { data } = await this.$axios.post(
        `${this.settings.generic_app_url}api/walkup-tracking/update-track`,
        payload
      );

      this.handleProspectResponse(data?.data);
    },

    async getTrackByProspect(walkupGuid) {
      const payload = {
        prospect_id: this.prospect?.id || null,
        walkup_guid: walkupGuid
      };

      const { data } = await this.$axios.post(
        `${this.settings.generic_app_url}api/walkup-tracking/track-by-prospect`,
        payload
      );

      this.handleProspectResponse(data?.data);
    },

    async getTrackByGuid(walkupGuid) {
      const payload = { walkup_guid: walkupGuid };

      const { data } = await this.$axios.post(
        `${this.settings.generic_app_url}api/walkup-tracking/track-by-guid`,
        payload
      );

      this.handleProspectResponse(data?.data);
    },

    async saveFirstTrack() {
      const params = new URLSearchParams(window.location.search);

      const payload = {
        prospect_id: this.prospect?.id || null,
        short_lead_guid: params.get("short_lead_guid") || null,
        ...this.getUtmParams()
      };

      const { data } = await this.$axios.post(
        `${this.settings.generic_app_url}api/walkup-tracking/first-track`,
        payload
      );

      this.handleProspectResponse(data?.data);
    },

    handleProspectResponse(data) {
      let loanUpdates = {};

      if (data?.walkup_guid) {
        loanUpdates = {
          walkup_guid: data.walkup_guid
        };

        localStorage.setItem(
          "walkup-tracking",
          JSON.stringify({ walkup_guid: data.walkup_guid })
        );
      }
      if (data?.prospect) { // walkup has prescreen prospect
        loanUpdates = {
          ...loanUpdates,
          prospectId: data.prospect.id,
        };

        this.setProspect(data.prospect);
      }
      if (data?.short_form_lead) { // walkup has short form lead prospect
        loanUpdates = {
          ...loanUpdates,
          shortFormLeadId: data.short_form_lead.id
        };

        // update lead api_key and campaign to api_key and campaign from short form lead
        const shortFormRawData = data.short_form_lead.raw_short_form_lead;
        this.setLeadProviderFromShortForm(shortFormRawData.api_key, shortFormRawData.campaign);
      }

      this.updateLoan(loanUpdates);
    },

    getUtmParams() {
      const params = new URLSearchParams(window.location.search);

      const utmParams = {
        utmSource: params.get("utm_source") || null,
        utmMedium: params.get("utm_medium") || null,
        utmCampaign: params.get("utm_campaign") || null,
        utmContent: params.get("utm_content") || null,
        utmTerm: params.get("utm_term") || null,
      };

      return utmParams;
    },

    async getShortFormLead() {
      const params = new URLSearchParams(window.location.search);
      const shortFormLeadGuid = params.get("short_lead_guid") || null;

      if (!shortFormLeadGuid) { return; }

      try {
        const { data, status } = await this.$axios.get(
          `${this.settings.generic_app_url}api/short-form/${shortFormLeadGuid}`
        );

        if (status === 200 && data && Object.keys(data).length) {
          /*
            * Short form requires only api_key, campaign, email, first_name, and last_name.
            * paytype may be empty here, so set a default to prevent validation errors later.
            */

          if ("paytype" in data && (data.paytype == null || data.paytype === "")) {
            data.paytype = "d"; // default to direct deposit if not set
          }
          if ("empphone" in data && (data.empphone == null || data.empphone === "")) {
            data.empphone = "(111)222-3333"; // default to dummy phone
          }
          this.updateLoan(data);
          this.setLeadProviderFromShortForm(data.api_key, data.campaign);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("Failed to fetch short form lead", error);
      }
    },

    setLeadProviderFromShortForm(apikey, campaign) {
      this.setApiKey(apikey);
      this.setCampaignId(campaign);
    }
  }
};
</script>

<style lang="scss">
.v-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.text-center {
  text-align: center;
}

#wrapper {
  width: 100%;
  height: 100%;
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-flow: column;

  #footer {
    flex-grow: 1; // this make sure footer take remaining space
  }
}

.help-block {
  display: block;
  font-size: 0.9em;
  font-style: italic;
  font-weight: 400;
  margin: 3px 0 0;

  &.with-errors {
    color: #d74e38;
  }
}
</style>
